import { useState } from "react";
import { IonAccordion, IonAccordionGroup, IonItem, IonLabel, IonSkeletonText } from "@ionic/react";
import { useQuery } from "@tanstack/react-query";
import { ActivityBase, ActivityTypes, LogEvent, RequestModel } from "model";
import { DataItemView } from "swing-components";

import { fetchActivityLog, translate } from "~utils";

/********* Types ********/
type ActivityLogProps = {
  request: RequestModel;
};

type FullStop = {
  symbolString: string;
  symbolLang: string;
};

const ACCEPT_MODE_ADMIN_CHOICE = "sub-req.accept-mode/admin-choice";
const ACCEPT_MODE_FIRST_RESPONSE = "sub-req.accept-mode/first-response";

/********* Component ********/
export function ActivityLog(props: ActivityLogProps) {
  const [openAccordion, setOpenAccordion] = useState<string>("");

  return (
    <div
      css={{
        borderRadius: "20px 20px 0 0",
        margin: "20px 0 20px 0",
      }}
    >
      <IonAccordionGroup
        css={{ margin: 0, borderRadius: 20, height: "100%" }}
        onIonChange={(e) => setOpenAccordion((e?.target?.value as string) || "")}
        value={openAccordion}
        expand="inset"
      >
        <IonAccordion value="activity-log" css={{ height: "100%", background: "white" }}>
          <IonItem slot="header" css={{ borderBottom: "solid 1px var(--swing-gray-200)" }}>
            <IonLabel css={{ fontWeight: "bolder" }}>
              <div css={{ fontWeight: 800, fontSize: 16, padding: "12px 0" }}>
                {translate("LABEL_ACTIVITY_LOG")}
              </div>
            </IonLabel>
          </IonItem>
          <div slot="content">
            <LogList isOpen={openAccordion === "activity-log"} {...props} />
          </div>
        </IonAccordion>
      </IonAccordionGroup>
    </div>
  );
}

type LogListProps = { isOpen: boolean; request: RequestModel };

function LogList(props: LogListProps) {
  const { isOpen, request } = props;

  const { id, acceptMode } = request;

  const {
    data: logs,
    isSuccess,
    isPending,
    error,
  } = useQuery({
    queryKey: ["activity-log", id],
    queryFn: async () => {
      const { data } = await fetchActivityLog({
        requestId: id || "",
        includeActivity: false,
      }).then((resp) => resp);
      return data;
    },
    enabled: isOpen,
    staleTime: Infinity,
    select: (data) => {
      return data.map((rawLog: ActivityBase) => {
        const logEvent = new LogEvent(rawLog, request.school.tzString);
        return logEvent;
      });
    },
  });

  if (isPending) {
    return (
      <div
        css={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          paddingTop: 8,
        }}
      >
        {Array.from({ length: 5 }).map((_, i) => {
          return (
            <div key={i} css={{ display: "flex", gap: 16, width: "100%", padding: "4px 12px" }}>
              <IonSkeletonText animated css={{ height: 28, flex: 0.4 }} />
              <IonSkeletonText animated css={{ height: 28, flex: 0.6 }} />
            </div>
          );
        })}
      </div>
    );
  }
  if (!isSuccess) {
    return <div css={{ padding: 16 }}>Something went wrong: {JSON.stringify(error?.message)}</div>;
  }

  return (
    <div css={{ padding: "16px 16px 0 16px" }} className="swing-data-item-list">
      {filterSubNotifications(logs).flatMap((logEvent, idx) =>
        getActivityLogDisplayStrings(logEvent, acceptMode).map((activityString, activityIdx) =>
          applySubLink(logEvent, `${idx}-${activityIdx}`, activityString),
        ),
      )}
    </div>
  );
}

/********* Helper Functions ********/

// create english fullstop
const enFullStop: FullStop = {
  symbolString: ".",
  symbolLang: "en",
};

function addFullStop(activityString: string) {
  const fullStop: FullStop = enFullStop;
  if (activityString.charAt(activityString.length - 1) !== fullStop.symbolString) {
    return `${activityString}${fullStop.symbolString}`;
  }
  return activityString;
}

export function getActivityLogDisplayStrings(logEvent: LogEvent, acceptMode: string): string[] {
  const action = logEvent.action;

  if (acceptMode === ACCEPT_MODE_ADMIN_CHOICE) {
    if (action === ActivityTypes.SUB_ADDED_TO_POOL) {
      return [translate("EVENT_LOG_LABEL_SUB_ADDED_TO_POOL_ADMIN_CHOICE", logEvent.subName)];
    }
    if (action === ActivityTypes.SUB_CHOSEN) {
      return [
        translate("EVENT_LOG_LABEL_SUB_CHOSEN_ADMIN_CHOICE", {
          authorName: logEvent.userName || "",
          subName: logEvent.subName,
        }),
      ];
    }
  }

  if (logEvent.action === ActivityTypes.SUB_REQ_CREATED) {
    return [translate("EVENT_LOG_LABEL_SUB_REQ_CREATED_NOTIFY"), logEvent.displayedActivityString];
  }

  if (acceptMode === ACCEPT_MODE_FIRST_RESPONSE && action === ActivityTypes.SUB_CHOSEN) {
    return [translate("EVENT_LOG_LABEL_SUB_CHOSEN_FIRST_RESPONSE", logEvent.subName)];
  }

  return [logEvent.displayedActivityString];
}

function applySubLink(logEvent: LogEvent, key: string, activityString: string) {
  const ActivityString = addFullStop(activityString);
  // if there is a sub in the event then update log text string
  // to implement a link to allow navigation to sub profile page
  // when clicking on the sub name
  if (logEvent.subId && logEvent.subName) {
    const activityWithSubLink = ActivityString.replaceAll(
      logEvent.subName,
      `<a class="swing-link swing-text" href="/#/active-requests/subs/${logEvent.subId}">${logEvent.subName}</a>`,
    );
    return (
      <DataItemView
        key={key}
        label={<h4 css={{ margin: 0, fontWeight: "bold" }}>{logEvent.dateString}</h4>}
      >
        <p
          // TODO: When replacing IonCardContent with the
          // Card component from swing-components, remove the !important
          // override. There is some styling related to the IonCardContent
          // that is overriding the font-size for <p></p> elements.
          css={{ fontSize: "16px !important", margin: 0 }}
          dangerouslySetInnerHTML={{ __html: activityWithSubLink }}
        ></p>
      </DataItemView>
    );
  }
  // otherwise no modifications are needed
  return (
    <DataItemView
      key={key}
      label={<h4 css={{ margin: 0, fontWeight: "bold" }}>{logEvent.dateString}</h4>}
    >
      <p
        // TODO: When replacing IonCardContent with the
        // Card component from swing-components, remove the !important
        // override. There is some styling related to the IonCardContent
        // that is overriding the font-size for <p></p> elements.
        css={{ fontSize: "16px !important", margin: 0 }}
      >
        {ActivityString}
      </p>
    </DataItemView>
  );
}

export function filterLatestStartDateAdjusted(logs: LogEvent[]): LogEvent[] {
  let latestLog: LogEvent | null = null;
  let latestTimestamp = 0;

  // Find the latest timestamp for adjusted start date log
  logs.forEach((log) => {
    if (log.action === ActivityTypes.REQUEST_START_DATE_ADJUSTED) {
      const timestamp = Date.parse(log.timestamp);
      if (timestamp > latestTimestamp) {
        latestTimestamp = timestamp;
        latestLog = log;
      }
    }
  });

  // Keep only the latest adjusted start date log, preserving order,
  // and remove TRIM_LONG_TERM_REQUEST logs (because it is a duplicate)
  return logs.filter((log) => {
    if (log.action === ActivityTypes.TRIM_LONG_TERM_REQUEST) {
      return false;
    }

    if (log.action === ActivityTypes.REQUEST_START_DATE_ADJUSTED) {
      return log === latestLog;
    }
    return true;
  });
}

function isASubNotifiedLog(log: LogEvent) {
  return log.action === ActivityTypes.SUB_NOTIFIED;
}

export function filterSubNotifications(logs: LogEvent[]) {
  const filteredLogs = filterLatestStartDateAdjusted(logs);

  const firstSubNotifiedLog = filteredLogs.find((log) => isASubNotifiedLog(log));
  if (firstSubNotifiedLog) {
    const subFilteredLogs = filteredLogs.filter((log) => !isASubNotifiedLog(log));
    const index = filteredLogs.findIndex((log) => isASubNotifiedLog(log));
    subFilteredLogs.splice(index, 0, firstSubNotifiedLog);
    return subFilteredLogs;
  }

  return filteredLogs;
}
